import { useState, useEffect } from 'react';
import { Card, Col, Container, Offcanvas, Row, Toast, ToastContainer } from 'react-bootstrap';

import { Link } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import {
    faPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';
import apiClient from 'helpers/apiService';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import Questionarie from './components/Questionarie';
import QuestionarieRO from './components/QuestionarieRO';
import { Assessment } from './components/DataDefinitions';
import ShareWithContactsModal from 'components/theach/contacts/ShareWithContactsModal';



const Table = () => {
    return (
        <div className="border-top">
            <AdvanceTable
                tableProps={{ className: 'phoenix-table fs-9' }}
                rowClassName="hover-actions-trigger btn-reveal-trigger"
            />
            <AdvanceTableFooter pagination className="py-4" />
        </div>
    );
};



const JobSkills = () => {
    const { t } = useTranslation();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },

        {
            label: t('Skills'),
            url: '/myskills'
        },
        {
            label: t('Job skills'),
            active: true
        }
    ];


    const [loading, setLoading] = useState(true);
    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const [assessments, setAssessments] = useState<Assessment[]>([]);
    const [assessment, setAssessment] = useState<Assessment>();

    // State to control the modal for assessment sharing
    const [showShareAssessment, setShowShareAssessment] = useState(false);
    const handleCloseShareAssessment = (emails: string[] | null) => {
        setShowShareAssessment(false);
        if (emails === null) return;
        if (emails.length === 0) return;
    };

    // State to control Toast messages
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const assessmentListTableColumns: ColumnDef<Assessment>[] = [
        {
            header: t('JobSkills.table.role'),
            cell: ({ row: { original } }) => {
                const { role } = original;
                return (
                    <Link to="#!" onClick={() => setAssessment(original)} className="fw-semi-bold">
                        {role}
                    </Link>
                );
            },
            meta: {
                cellProps: { className: 'white-space-nowrap py-4' },
                headerProps: { style: { width: '25%' } }
            }
        },

        {
            header: 'Date',
            accessorKey: 'inserted_at',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '12%' }, className: 'ps-3' }
            }
        },


    ];

    const table = useAdvanceTable({
        data: assessments,
        columns: assessmentListTableColumns,
        pageSize: 6,
        pagination: true,
        sortable: true
    });


    const fetchData = async () => {
        try {
            const { data } = await apiClient.get('/v1/skills/assessments/job-skills');
            setAssessments(data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (questionarie: any) => {

        const formData = new FormData();
        formData.append('questionarieForm', JSON.stringify(questionarie));
        try {
            const response = await apiClient.post(
                `/v1/skills/assessment/job-skills`, formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );
            fetchData();
            setToastMessage(response.data['message'])
            setShowToast(true);
            setShow(false);
        } catch (error) {
            console.error('Request failed:', error);
            setToastMessage("Unable to save data to the database");
        }

    };

    const handleShareAssessment = async (assessment_id: number) => {
        setShowShareAssessment(true);
    };

    useEffect(() => {
        fetchData();
    }, []);



    if (loading) {
        return <div>Loading...</div>;
    }



    return (
        <div>

            <PageBreadcrumb items={breadcrumbItems} />

            <ToastContainer className="p-3" position='top-center'>
                <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide>
                    <Toast.Body className=''>{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>

            {/* Modal to start a new assment */}
            <Offcanvas show={show} onHide={handleClose} backdrop="static" placement="end" style={{ 'width': '35%' }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className='fs-5' >{t("JobSkills.title")}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Questionarie assessment_type="job-skills" onSubmit={handleSubmit} />
                </Offcanvas.Body>
            </Offcanvas>

            {/* Modal to share an existing assessment */}
            <ShareWithContactsModal show={showShareAssessment} onClose={handleCloseShareAssessment} />

            <Container fluid>
                <Row className="mt-3">
                    <Col className="p-2">
                        <Card className='border border-light rounded-5' >
                            <Card.Body>
                                <AdvanceTableProvider {...table}>
                                    <Row className="g-3 justify-content-between mb-4">
                                        <Col xs="auto">
                                            <Button
                                                variant="primary"
                                                className="me-4"
                                                startIcon={
                                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                }
                                                onClick={handleShow}
                                            >
                                                {t('JobSkills.new_assessment')}
                                            </Button>


                                        </Col>

                                    </Row>
                                    <Table />
                                </AdvanceTableProvider>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col className="p-2">
                        {assessment && <QuestionarieRO title={assessment.role + " - " + assessment.inserted_at}
                            data={JSON.parse(assessment.assessment)}
                            assessment_id={assessment.assessment_id}
                            onShare={handleShareAssessment} />}
                    </Col>
                </Row >
            </Container >

        </div >
    );


};

export default JobSkills;
